import { careIndustryPageKeys } from '../content/careIndustryLabels.js';
import { retailIndustryPageKeys } from '../content/retailIndustryLabels.js';
import type { StaticPageKey } from '../content/staticPageContent.js';
import { industryPageLabel, newIndustryPageKeys } from '../content/industryPages.js';

export type EditorPageKey = 'landing' | StaticPageKey;
export interface LandingEditorPageDefinition {
  key: EditorPageKey;
  label: string;
  path: string;
}

export const editorPages: readonly LandingEditorPageDefinition[] = [
  { key: 'landing', label: 'Landingpage', path: '/' },
  { key: 'bewerben', label: 'Bewerben', path: '/bewerben' },
  { key: 'unternehmen', label: 'Unternehmen', path: '/unternehmen' },
  { key: 'pflege', label: 'Branche Pflege', path: '/branchen/pflege' },
  { key: 'gastro', label: 'Branche Gastronomie', path: '/branchen/gastro' },
  { key: 'einzelhandel', label: 'Branche Einzelhandel', path: '/branchen/einzelhandel' },
  { key: 'handwerk', label: 'Branche Handwerk', path: '/branchen/handwerk' },
  ...careIndustryPageKeys.map(key => ({key,label:`Branche ${industryPageLabel(key,'de')}`,path:`/branchen/${key}`})),
  ...retailIndustryPageKeys.map(key => ({key,label:`Branche ${industryPageLabel(key,'de')}`,path:`/branchen/${key}`})),
  ...newIndustryPageKeys.map(key => ({ key, label: `Branche ${industryPageLabel(key, 'de')}`, path: `/branchen/${key}` })),
  { key: 'globus', label: 'Globus', path: '/globus' },
  { key: 'ablauf', label: 'Ablauf', path: '/ablauf' },
  { key: 'ueber-vivaporta', label: 'Über Viva Porta', path: '/ueber-vivaporta' },
  { key: 'team', label: 'Team', path: '/team' },
  { key: 'kontakt', label: 'Kontakt', path: '/kontakt' },
  { key: 'impressum', label: 'Impressum', path: '/impressum' },
];

export type EditorNavigationTarget =
  | { kind: 'page'; pageKey: EditorPageKey; anchorId?: string }
  | { kind: 'external'; href: string }
  | { kind: 'none' };

const editorPathAliases: Readonly<Record<string, string>> = {
  '/mission': '/ueber-vivaporta',
  '/ueber-uns': '/ueber-vivaporta',
};

const normalizePath = (path: string) => {
  if (!path) return '/';
  const normalized = path.startsWith('/') ? path : `/${path}`;
  return normalized.length > 1 ? normalized.replace(/\/$/u, '') : normalized;
};

const isVivaPortaHost = (hostname: string) =>
  /(^|\.)vivaporta(?:-medical)?\.(?:org|de)$/iu.test(hostname);

export const resolveEditorNavigationTarget = (href: string, currentOrigin: string): EditorNavigationTarget => {
  const rawHref = href.trim();
  if (!rawHref) return { kind: 'none' };
  if (rawHref.startsWith('#')) {
    return { kind: 'page', pageKey: 'landing', anchorId: rawHref.slice(1) || undefined };
  }
  if (/^(mailto:|tel:)/iu.test(rawHref)) return { kind: 'external', href: rawHref };

  let url: URL;
  try {
    url = new URL(rawHref, currentOrigin);
  } catch {
    return { kind: 'none' };
  }
  const normalizedPath = normalizePath(url.pathname);
  const canonicalPath = editorPathAliases[normalizedPath] ?? normalizedPath;
  const page = editorPages.find(candidate => normalizePath(candidate.path) === canonicalPath);
  const currentHostname = new URL(currentOrigin).hostname;
  const canOpenInsideEditor = url.hostname === currentHostname || isVivaPortaHost(url.hostname);
  if (page && canOpenInsideEditor) {
    return { kind: 'page', pageKey: page.key, ...(url.hash ? { anchorId: url.hash.slice(1) } : {}) };
  }
  if (url.origin !== currentOrigin) return { kind: 'external', href: rawHref };
  return { kind: 'none' };
};
